import type { PlanPriceStage, SubscriptionPlan } from '@thinkalpha/access-ws-client/contracts/subscription.js';
import { humanizePeriod } from '@thinkalpha/ui-core/time/index';
import { t } from 'i18next';

export type { PlanPriceStage } from '@thinkalpha/access-ws-client/contracts/subscription.js';

export const COMING_SOON_ADVERTISING_SLUG = 'coming-soon';

export interface PlanPricingDisplayItem {
    readonly stage: PlanPriceStage;
    /** A second ongoing stage establishes the regular-price comparison for the first. */
    readonly comparison?: PlanPriceStage;
}

export interface PlanCardPresentation {
    readonly id: string;
    readonly name: string;
    readonly currency: string;
    readonly description: string;
    readonly pricingStages: readonly PlanPriceStage[];
    readonly inviteCodeRequired: boolean;
    readonly includedPlanNames: readonly string[];
    readonly features: readonly string[];
}

export function isComingSoonPlan(plan: Pick<SubscriptionPlan, 'advertisingSlugs'>): boolean {
    return plan.advertisingSlugs.includes(COMING_SOON_ADVERTISING_SLUG);
}

/**
 * Adds presentation-only plan metadata while retaining the generated pricing-stage contract.
 * The API has already resolved promotional eligibility for the requesting user.
 */
export function toPlanCardPresentation(
    plan: SubscriptionPlan,
    allPlans: readonly SubscriptionPlan[],
): PlanCardPresentation {
    const includedPlanNames = plan.includeFeaturesFromPlans
        .map((id) => allPlans.find((candidate) => candidate.id === id)?.name)
        .filter((name): name is string => name != null);

    return {
        id: plan.id,
        name: plan.name,
        currency: plan.currency,
        description: plan.description ?? '',
        pricingStages: plan.pricing,
        inviteCodeRequired: plan.inviteCodeRequired,
        includedPlanNames,
        features: plan.features ?? [],
    };
}

export function getTerminalPlanPriceStage(stages: readonly PlanPriceStage[]): PlanPriceStage | undefined {
    return stages.find((stage) => stage.period === null) ?? stages.at(-1);
}

export function toPlanPricingDisplayItems(stages: readonly PlanPriceStage[]): PlanPricingDisplayItem[] {
    const items: PlanPricingDisplayItem[] = [];

    for (let index = 0; index < stages.length; index += 1) {
        const stage = stages[index];
        const nextStage = stages[index + 1];

        if (stage.period === null && nextStage?.period === null) {
            items.push({ stage, comparison: nextStage });
            index += 1;
        } else {
            items.push({ stage });
        }
    }

    return items;
}

export function formatPlanPriceStageInterval(stage: PlanPriceStage): string | undefined {
    return stage.billingInterval === null ? undefined : humanizePeriod(stage.billingInterval);
}

export function formatPlanPriceStageDuration(stage: PlanPriceStage): string | undefined {
    if (stage.period === null) return undefined;

    const isDaysOnly = stage.period.years() === 0 && stage.period.months() === 0;
    if (isDaysOnly) {
        const days = stage.period.days();
        return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    const duration = humanizePeriod(stage.period);
    return /^\d/.test(duration) ? duration : `1 ${duration}`;
}

export function formatPlanPriceStageComparison(stage: PlanPriceStage, currency: string): string {
    const price = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(stage.price);
    const name = stage.name ? `${stage.name} ` : '';
    const interval = formatPlanPriceStageInterval(stage);
    const cadence = interval ? `/${interval}` : ` ${t('subscription::plan-card::one-time')}`;

    return `${name}${price}${cadence}`;
}
